/**
 * Contracts for module "identity". Owner: identity.
 * Put zod request/response/event schemas for this module here; other modules and the portal
 * import them as `import { ... } from '@qm/contracts/identity'`. Never import another module's internals.
 */
import { z } from 'zod';

// ---------------------------------------------------------------------------------------------------------
// Session policy (NFR X-07). The API enforces these; the portal only mirrors them for warnings.
// ---------------------------------------------------------------------------------------------------------
export const SESSION_IDLE_MS = 30 * 60 * 1000;
export const SESSION_ABSOLUTE_MS = 12 * 60 * 60 * 1000;
/**
 * "Keep me signed in on this device" (owner decision 31 Oct 2026): a customer or agency session opted in from the
 * installed portal app (POST /v1/identity/session/keep-signed-in) lasts 24 h idle / 7 days absolute. Staff and
 * Super Admin sessions (including view-as) never opt in. Opt-in is accepted only this soon after sign-in.
 */
export const SESSION_KEEP_IDLE_MS = 24 * 60 * 60 * 1000;
export const SESSION_KEEP_ABSOLUTE_MS = 7 * 24 * 60 * 60 * 1000;
export const SESSION_KEEP_OPT_IN_WINDOW_MS = 10 * 60 * 1000;
/** Revocation is visible on the next request (D1 is authoritative, no cache); the target is <= 60 s. */
export const SESSION_REVOCATION_TARGET_MS = 60 * 1000;
export const MAGIC_LINK_TTL_MS = 15 * 60 * 1000;
export const ONBOARDING_LINK_TTL_MS = 7 * 24 * 60 * 60 * 1000;

/** Host-only session cookie (no Domain attribute, so portal/partners/admin never share it). */
export const SESSION_COOKIE = '__Host-qm_sid';
/** Short-lived browser binding for the Entra OIDC round trip (login CSRF protection). */
export const OIDC_BINDING_COOKIE = '__Host-qm_oidc';

/**
 * Response header set by qm-api when a request carried a session cookie that is no longer valid.
 * The portal reacts by showing sign-in and broadcasting sign-out to every tab.
 */
export const SESSION_STATE_HEADER = 'x-qm-session-state';
export const SessionStateSchema = z.enum([
  'expired', 'idle_expired', 'revoked', 'host_mismatch', 'mfa_required', 'mfa_enrolment_required', 'no_membership',
]);
export type SessionState = z.infer<typeof SessionStateSchema>;

/** BroadcastChannel name the portal uses for cross-tab sign-out. */
export const SESSION_BROADCAST_CHANNEL = 'qm-session';

export interface BroadcastLike { postMessage(message: unknown): void; onmessage: ((ev: { data: unknown }) => void) | null }

/**
 * Cross-tab sign-out helper for the portal shell (no DOM dependency so it is unit-testable).
 * Call `observe(response)` on every BFF response; when the API reports an invalid session every tab gets
 * `onSignedOut(reason)`, including the one that saw the 401.
 */
export function createCrossTabSignOut(channel: BroadcastLike, onSignedOut: (reason: SessionState) => void) {
  channel.onmessage = (ev) => {
    const parsed = z.object({ type: z.literal('signed_out'), reason: SessionStateSchema }).safeParse(ev.data);
    if (parsed.success) onSignedOut(parsed.data.reason);
  };
  return {
    observe(res: { status: number; headers: { get(name: string): string | null } }): boolean {
      if (res.status !== 401) return false;
      // No header at all means the request never carried a session cookie (never signed in), not a session that
      // just died - the API only sends this header when it actually invalidated one (see signalSessionState).
      const raw = res.headers.get(SESSION_STATE_HEADER);
      if (raw === null) return false;
      const state = SessionStateSchema.safeParse(raw);
      const reason: SessionState = state.success ? state.data : 'expired';
      channel.postMessage({ type: 'signed_out', reason });
      onSignedOut(reason);
      return true;
    },
    signOut(reason: SessionState = 'revoked') {
      channel.postMessage({ type: 'signed_out', reason });
      onSignedOut(reason);
    },
  };
}

// ---------------------------------------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------------------------------------
export const EmailSchema = z.string().trim().toLowerCase().email().max(254);

// ---------------------------------------------------------------------------------------------------------
// Staff-domain surface hint (owner request 27 Sep 2026): every sign-in screen (customer, partner, admin)
// classifies the email's domain only, so a @quixohub.com address typed into the wrong
// surface can be pointed at admin.quixomarketing.com. This is NOT an account lookup: it never queries a user
// table, so it cannot be used to confirm or deny that an account exists (contrast with the customer/partner
// `wrong_portal` email in issueMagicLink, which does look up an account but only ever emails that account's
// own owner, never the caller). The list is not secret - it ships in the client bundle either way - so this
// pure function is safe to run on the client (immediate, no round trip) or the server (defence in depth /
// non-JS clients) from the same single source of truth.
// ---------------------------------------------------------------------------------------------------------
/**
 * Domains whose mailboxes belong to Quixo staff; only staff sign in, through Entra ID, on the admin host.
 * quixomarketing.com is not one (owner decision 22 Oct 2026): the owner signs in to the customer portal with
 * that address, and sending it to Entra blocked customer sign-in. Staff access itself is decided by Entra, not here.
 */
export const STAFF_EMAIL_DOMAINS = ['quixohub.com'] as const;

/**
 * Classify an email's domain: 'admin' when it belongs to a staff domain (subdomains included, e.g.
 * name@mail.quixohub.com), otherwise null. Customer and partner emails are indistinguishable by domain alone,
 * so this never guesses between them - only "is this staff" is decidable this way. Pure string matching: no
 * DB, no network, no side effects.
 */
export function surfaceForEmailDomain(email: string): 'admin' | null {
  const at = email.lastIndexOf('@');
  if (at < 0) return null;
  const domain = email.slice(at + 1).trim().toLowerCase();
  if (!domain) return null;
  return STAFF_EMAIL_DOMAINS.some((d) => domain === d || domain.endsWith(`.${d}`)) ? 'admin' : null;
}

/** POST /v1/identity/surface-hint request body. Deliberately lenient (not EmailSchema): the caller may send a
 * partial address while the person is still typing, and only the domain half is ever inspected. */
export const SurfaceHintRequestSchema = z.object({ email: z.string().trim().min(1).max(254) });
export type SurfaceHintRequest = z.infer<typeof SurfaceHintRequestSchema>;
export const SurfaceHintResponseSchema = z.object({ surface: z.literal('admin').nullable() });
export type SurfaceHintResponse = z.infer<typeof SurfaceHintResponseSchema>;

export const MagicLinkRequestSchema = z.object({ email: EmailSchema, locale: z.enum(['en', 'ar']).optional() });
export type MagicLinkRequest = z.infer<typeof MagicLinkRequestSchema>;

/** Website on the sign-up form: optional; a bare domain gets https://; only http(s) URLs are accepted. */
export const SignupWebsiteSchema = z.preprocess(
  (v) => {
    if (typeof v !== 'string') return v;
    const s = v.trim();
    if (!s) return undefined;
    return /^[a-z][a-z0-9+.-]*:/i.test(s) ? s : `https://${s}`;
  },
  z.string().max(200).url().refine((u) => /^https?:\/\//i.test(u), { message: 'Website must be an http(s) URL' }).optional(),
);

/**
 * Self-serve customer sign-up (portal /signup). Always answered 202 with the same body (no account enumeration):
 * a new email gets a workspace + pending owner and a verification link; a known email gets a normal sign-in link.
 */
export const SignupRequestSchema = z.object({
  name: z.string().trim().min(1).max(120),
  email: EmailSchema,
  company: z.string().trim().min(1).max(120),
  website: SignupWebsiteSchema,
  country: z.string().trim().toUpperCase().regex(/^[A-Z]{2}$/).default('AE'),
  consent: z.literal(true),
  locale: z.enum(['en', 'ar']).optional(),
});
export type SignupRequest = z.infer<typeof SignupRequestSchema>;
/** Version of the terms + privacy notice the sign-up consent refers to (recorded in the audit log). */
export const SIGNUP_TERMS_VERSION = '2026-09';
/** Plan code bound to self-serve workspaces until the owner picks a plan during onboarding. */
export const SELF_SERVE_PLAN_CODE = 'self_serve';

export const TokenSchema = z.string().min(20).max(200).regex(/^[A-Za-z0-9_-]+$/);
export const MagicLinkConsumeSchema = z.object({ token: TokenSchema });

export const SwitchTenantSchema = z.object({ tenantId: z.string().min(1).max(64) });

export const TotpCodeSchema = z.object({ code: z.string().trim().regex(/^\d{6}$/) });
export const RecoveryCodeSchema = z.object({ code: z.string().trim().min(8).max(32) });
export const TotpConfirmSchema = z.object({ factorId: z.string().min(1).max(64), code: z.string().trim().regex(/^\d{6}$/) });

export const RevokeAllSchema = z.object({ includeCurrent: z.boolean().default(true) });

// ---------------------------------------------------------------------------------------------------------
// Passkeys (WebAuthn). Flag: identity.passkeys (platform level, default OFF). Bodies carry the browser's
// PublicKeyCredential JSON (as produced by @simplewebauthn/browser); the server validates it with
// @simplewebauthn/server. Limits keep hostile bodies small.
// ---------------------------------------------------------------------------------------------------------
export const PASSKEYS_FLAG = 'identity.passkeys';
/** Ceremony challenges are single use and expire after this long. */
export const PASSKEY_CHALLENGE_TTL_MS = 5 * 60 * 1000;
export const MAX_PASSKEYS_PER_USER = 10;

const B64Url = (max: number) => z.string().min(1).max(max).regex(/^[A-Za-z0-9_-]+={0,2}$/);
export const PasskeyNameSchema = z.string().trim().min(1).max(64);
const Transports = z.array(z.enum(['ble', 'cable', 'hybrid', 'internal', 'nfc', 'smart-card', 'usb'])).max(8);
const credentialBase = {
  id: B64Url(1400),
  rawId: B64Url(1400),
  type: z.literal('public-key'),
  clientExtensionResults: z.record(z.string(), z.unknown()).default({}),
  authenticatorAttachment: z.enum(['platform', 'cross-platform']).optional(),
};

export const PasskeyRegistrationVerifySchema = z.object({
  name: PasskeyNameSchema.optional(),
  response: z.object({
    ...credentialBase,
    response: z.object({
      clientDataJSON: B64Url(4096),
      attestationObject: B64Url(16384),
      transports: Transports.optional(),
      authenticatorData: B64Url(8192).optional(),
      publicKey: B64Url(4096).optional(),
      publicKeyAlgorithm: z.number().int().optional(),
    }),
  }),
});
export type PasskeyRegistrationVerify = z.infer<typeof PasskeyRegistrationVerifySchema>;

export const PasskeyAuthenticationVerifySchema = z.object({
  response: z.object({
    ...credentialBase,
    response: z.object({
      clientDataJSON: B64Url(4096),
      authenticatorData: B64Url(8192),
      signature: B64Url(2048),
      userHandle: B64Url(512).optional(),
    }),
  }),
});
export type PasskeyAuthenticationVerify = z.infer<typeof PasskeyAuthenticationVerifySchema>;

export const PasskeyRenameSchema = z.object({ name: PasskeyNameSchema });

export const PasskeyViewSchema = z.object({
  id: z.string(),
  name: z.string(),
  deviceType: z.enum(['singleDevice', 'multiDevice']),
  backedUp: z.boolean(),
  transports: z.array(z.string()),
  createdAt: z.string(),
  lastUsedAt: z.string().nullable(),
});
export type PasskeyView = z.infer<typeof PasskeyViewSchema>;
/** POST /passkeys/registration/verify: recoveryCodes is present (once) when the person held no unused codes. */
export const PasskeyRegisteredSchema = PasskeyViewSchema.extend({ recoveryCodes: z.array(z.string()).optional() });
export type PasskeyRegistered = z.infer<typeof PasskeyRegisteredSchema>;

export const PlanCodeSchema = z.string().min(2).max(40).regex(/^[a-z0-9][a-z0-9_-]*$/);
export const OnboardingLinkCreateSchema = z.object({
  tenantId: z.string().min(1).max(64).regex(/^[A-Za-z0-9_-]+$/),
  planCode: PlanCodeSchema,
  email: EmailSchema,
});
export type OnboardingLinkCreate = z.infer<typeof OnboardingLinkCreateSchema>;
/** Any plan the client sends is ignored; the link's bound plan wins (QM-D04-001 plan tamper). */
export const OnboardingRedeemSchema = z.object({ token: TokenSchema }).passthrough();

export const ONBOARDING_STEPS = ['profile', 'channels', 'plan', 'checklist'] as const;
export type OnboardingStep = (typeof ONBOARDING_STEPS)[number];
export const CHANNELS = ['whatsapp', 'meta', 'google_ads', 'gbp', 'linkedin', 'tiktok', 'snap', 'website', 'email'] as const;

export const OnboardingProfileSchema = z.object({
  businessName: z.string().trim().min(1).max(120),
  industry: z.string().trim().min(1).max(80),
  country: z.string().trim().length(2).toUpperCase(),
  website: z.string().url().max(200).optional(),
  phone: z.string().trim().max(32).optional(),
  preferredLocale: z.enum(['en', 'ar']).default('en'),
});
export const OnboardingChannelsSchema = z.object({ channels: z.array(z.enum(CHANNELS)).max(CHANNELS.length) });
export const OnboardingPlanAckSchema = z.object({ acknowledged: z.literal(true) });
export const OnboardingChecklistSchema = z.object({ acknowledged: z.literal(true) });

/** staff_content: website CMS editor (edits and publishes pages, items and media; owner decision 25 Sep 2026). */
export const StaffRoleSchema = z.enum(['staff_admin', 'staff_ops', 'staff_support', 'staff_finance', 'staff_readonly', 'staff_content']);
export const EntraRoleMapSchema = z.object({ groupRef: z.string().min(1).max(128), role: StaffRoleSchema });
/** The console saves the whole mapping list at once (upsert only; removing a mapping is a separate DELETE). */
export const EntraRoleMapManySchema = z.object({ entries: z.array(EntraRoleMapSchema).min(1).max(100) }).strict();

export const PartnerAccountCreateSchema = z.object({ email: EmailSchema, partnerId: z.string().min(1).max(64), displayName: z.string().max(120).optional() });

/** Microsoft Graph change notification (subset). */
export const GraphNotificationSchema = z.object({
  value: z.array(z.object({
    clientState: z.string().optional(),
    changeType: z.string().optional(),
    resource: z.string().optional(),
    resourceData: z.object({ id: z.string().optional() }).passthrough().optional(),
  }).passthrough()).max(1000),
});

// ---------------------------------------------------------------------------------------------------------
// Responses
// ---------------------------------------------------------------------------------------------------------
export const MembershipViewSchema = z.object({
  tenantId: z.string().nullable(),
  partnerId: z.string().nullable(),
  role: z.string(),
  status: z.enum(['active', 'pending', 'suspended', 'revoked']),
  /** display name of the workspace (tenants.name), for the workspace switcher (QM-D02-004) */
  tenantName: z.string().nullable().optional(),
});

export const MeSchema = z.object({
  userId: z.string(),
  email: z.string(),
  realm: z.enum(['customer', 'partner', 'staff']),
  surface: z.enum(['customer', 'partner', 'admin']),
  tenantId: z.string().nullable(),
  partnerId: z.string().nullable(),
  roles: z.array(z.string()),
  memberships: z.array(MembershipViewSchema),
  partnerState: z.enum(['approved', 'pending_approval']).nullable(),
  /**
   * DEC-10 account state of the active customer workspace (rule table: @qm/tenancy/access-states). `banner` is set while
   * a failed payment is inside the 14-day grace ('payment_past_due'), with `graceUntil` its end. null off a customer tenant.
   */
  access: z.object({
    state: z.enum(['verified_unpaid', 'active', 'past_due', 'suspended']),
    banner: z.enum(['payment_past_due']).nullable(),
    graceUntil: z.string().nullable(),
  }).nullable().optional(),
  mfa: z.object({
    state: z.enum(['none', 'pending', 'verified', 'enrol_required']), enrolled: z.boolean(), required: z.boolean(),
    /** second factors this user can use now ('passkey' only while identity.passkeys is on) */
    methods: z.array(z.enum(['totp', 'passkey'])).optional(),
  }),
  session: z.object({ id: z.string(), idleExpiresAt: z.string(), absoluteExpiresAt: z.string(), keepSignedIn: z.boolean().optional() }),
});
export type Me = z.infer<typeof MeSchema>;

export const SessionViewSchema = z.object({
  id: z.string(),
  current: z.boolean(),
  surface: z.string(),
  authMethod: z.string(),
  userAgent: z.string().nullable(),
  createdAt: z.string(),
  lastSeenAt: z.string(),
  idleExpiresAt: z.string(),
  absoluteExpiresAt: z.string(),
});
export type SessionView = z.infer<typeof SessionViewSchema>;

// ---------------------------------------------------------------------------------------------------------
// Queue events (body shape: { type, tenantId, idempotencyKey, payload })
// ---------------------------------------------------------------------------------------------------------
/** MESSAGING_QUEUE: transactional identity email. payload.url carries a one-time token: never log payloads. */
export const IdentityEmailEventSchema = z.object({
  type: z.literal('identity.email.send'),
  tenantId: z.string().nullable(),
  idempotencyKey: z.string(),
  payload: z.object({
    template: z.enum(['magic_link', 'wrong_portal', 'no_account', 'onboarding_link', 'continue_setup']),
    purpose: z.enum(['verify_email', 'continue_setup', 'view_audit']).optional(),
    to: z.string(),
    url: z.string().url(),
    locale: z.enum(['en', 'ar']),
    expiresAt: z.string().nullable(),
  }),
});
export type IdentityEmailEvent = z.infer<typeof IdentityEmailEventSchema>;

/** SYSTEM_QUEUE: tenancy activates the tenant when onboarding completes (QM-D04-002). */
export const OnboardingCompletedEventSchema = z.object({
  type: z.literal('identity.onboarding.completed'),
  tenantId: z.string(),
  idempotencyKey: z.string(),
  payload: z.object({ onboardingId: z.string(), planCode: z.string(), ownerUserId: z.string(), completedAt: z.string() }),
});
export type OnboardingCompletedEvent = z.infer<typeof OnboardingCompletedEventSchema>;

/** SYSTEM_QUEUE: emitted when staff access is removed or changed via Entra (QM-C04-004). */
export const StaffAccessChangedEventSchema = z.object({
  type: z.literal('identity.staff.access_changed'),
  tenantId: z.null(),
  idempotencyKey: z.string(),
  payload: z.object({ userId: z.string(), change: z.enum(['deprovisioned', 'roles_changed']), roles: z.array(z.string()) }),
});
